/**
 * Un'immagine a schermo pieno, da ingrandire.
 *
 * La piantina dello stage nel diario era una miniatura alta 14 rem: il
 * tiratore ci cliccava sopra aspettandosi di vederla bene e non succedeva
 * niente. Il viewport dell'app ha `user-scalable=no`, quindi nemmeno il
 * pinch aiuta: lo zoom va dato dall'interfaccia.
 *
 * Tre livelli a bottone e il doppio tocco per alternare 1× / 2.5×; a zoom 1
 * l'immagine si adatta allo schermo, oltre si scorre dentro il riquadro.
 * Nessuna libreria: è un'immagine in un contenitore che scrolla.
 */
import { useEffect, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";

const LEVELS = [1, 2, 3, 4];

export function ImageZoomDialog({ open, onOpenChange, src, alt, title }) {
	const [zoom, setZoom] = useState(1);

	// Ogni immagine riparte adattata allo schermo.
	useEffect(() => {
		if (open) setZoom(1);
	}, [open, src]);

	const step = (direction) => {
		const index = LEVELS.indexOf(zoom);
		const next = LEVELS[Math.min(LEVELS.length - 1, Math.max(0, index + direction))];
		setZoom(next);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="bg-card border-border rounded-sm w-screen max-w-none h-[100dvh] max-h-[100dvh] sm:w-[calc(100vw-2rem)] sm:max-w-5xl sm:h-[calc(100dvh-2rem)] p-3 flex flex-col gap-2 overflow-hidden">
				<DialogHeader className="shrink-0 pr-8">
					<DialogTitle className="font-heading text-base truncate">
						{title || alt || "Immagine"}
					</DialogTitle>
					<DialogDescription className="sr-only">{alt || ""}</DialogDescription>
				</DialogHeader>

				<div
					className="flex-1 min-h-0 overflow-auto rounded-sm bg-white"
					style={{ touchAction: "pan-x pan-y" }}
					onDoubleClick={() => setZoom((z) => (z === 1 ? 2.5 : 1))}
				>
					{src && (
						<img
							src={src}
							alt={alt || ""}
							draggable={false}
							className="block select-none"
							style={
								zoom === 1
									? { width: "100%", height: "100%", objectFit: "contain" }
									: { width: `${zoom * 100}%`, height: "auto", maxWidth: "none" }
							}
						/>
					)}
				</div>

				<div className="shrink-0 flex items-center justify-center gap-1">
					<Button
						type="button"
						size="icon"
						variant="ghost"
						className="h-8 w-8 rounded-sm"
						onClick={() => step(-1)}
						disabled={zoom <= LEVELS[0]}
						aria-label="Riduci"
					>
						<ZoomOut className="w-4 h-4" />
					</Button>
					{LEVELS.map((level) => (
						<button
							key={level}
							type="button"
							onClick={() => setZoom(level)}
							className={`h-8 min-w-[2.5rem] px-2 rounded-sm text-xs tabular-nums transition-colors ${
								zoom === level
									? "bg-volt/15 text-volt font-medium"
									: "text-muted-foreground hover:text-foreground"
							}`}
						>
							{level}×
						</button>
					))}
					<Button
						type="button"
						size="icon"
						variant="ghost"
						className="h-8 w-8 rounded-sm"
						onClick={() => step(1)}
						disabled={zoom >= LEVELS[LEVELS.length - 1]}
						aria-label="Ingrandisci"
					>
						<ZoomIn className="w-4 h-4" />
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
